import React from 'react';
import { Button } from '@/components/ui/button';
import { FilterX, FolderOpen, SearchX, X } from 'lucide-react';
import type { EmptyStateType } from '@/components/Pages/ActiveProjects/useActiveProjects';

interface ProjectsEmptyStateProps {
  emptyStateType: EmptyStateType;
  searchQuery: string;
  onClearSearch: () => void;
  onClearAllFilters: () => void;
}

export const ProjectsEmptyState: React.FC<ProjectsEmptyStateProps> = ({
  emptyStateType,
  searchQuery,
  onClearSearch,
  onClearAllFilters,
}) => {
  const emptyStates = {
    'no-data': {
      icon: FolderOpen,
      title: 'No projects yet',
      description: 'There are no projects to display.',
      action: null
    },
    'no-search': {
      icon: SearchX,
      title: `No projects match "${searchQuery}"`,
      description: 'Try adjusting your search terms or check for typos.',
      action: (
        <Button variant="outline" className="mt-4" onClick={onClearSearch}>
          <X className="h-4 w-4 mr-2" />
          Clear Search
        </Button>
      )
    },
    'no-filters': {
      icon: FilterX,
      title: 'No projects match your filters',
      description: 'Try adjusting your filter criteria to see more results.',
      action: (
        <Button variant="outline" className="mt-4" onClick={onClearAllFilters}>
          <X className="h-4 w-4 mr-2" />
          Clear All Filters
        </Button>
      )
    }
  };

  const state = emptyStates[emptyStateType];
  const Icon = state.icon;

  return (
    <div className="flex flex-col items-center justify-center py-16 px-4">
      <div className="rounded-full bg-muted p-6 mb-4">
        <Icon className="h-12 w-12 text-muted-foreground" />
      </div>
      <h3 className="text-xl font-semibold mb-2">{state.title}</h3>
      <p className="text-sm text-muted-foreground text-center max-w-md mb-4">
        {state.description}
      </p>
      {state.action}
    </div>
  );
};
